import { useTranslation } from "react-i18next";
import {useAppDispatch, useAppSelector} from "../app/hooks";
import {RootState} from "../app/store";
import {Outlet, useNavigate, useParams} from "react-router-dom";
import React, {useEffect} from "react";
import {
    clearErrorNoOrgCaretakers,
    deleteNoOrgCaretaker,
    fetchNoOrgCaretakers,
    selectNoOrgCaretaker,
    startDeleteNoOrgCaretaker, updateNoOrgCaretakerSearchTerm
} from "../features/caretakersNoOrgSlice";
import {RouteNames} from "../routes";
import {CaretakerResponseDto, UserAccountDto} from "../api/client-axios";
import {ColumnsType} from "antd/lib/table/interface";
import {formatPhone} from "../utils/formatPhone";
import { Table } from "antd";

import { CrudModals } from "../components/CrudModals";
import { CrudToolbar } from "../components/CrudToolbar";
import { EmptyState } from "../components/EmptyState";
import { TableSkeleton } from "../components/TableSkeleton";

export const CaretakersIndividualsPage = () => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const caretakers = useAppSelector((state: RootState) => state.caretakersNoOrg.caretakers);
    const selectedCaretaker = useAppSelector((state: RootState) => state.caretakersNoOrg.selectedCaretaker);
    const searchTerm = useAppSelector((state: RootState) => state.caretakersNoOrg.searchTerm);
    const countries = useAppSelector((state: RootState) => state.country.countries);
    const status = useAppSelector((state: RootState) => state.caretakersNoOrg.status);
    const error = useAppSelector((state: RootState) => state.caretakersNoOrg.error);
    const navigate = useNavigate();
    const params = useParams();

    useEffect(() => {
        if (status === 'idle') {
            dispatch(fetchNoOrgCaretakers({}))
        }
    });

    if (params.caretakerId !== undefined) {
        return <Outlet/>
    }

    const onWarnDeleteCaretaker = () => {
        if (selectedCaretaker != null) {
            dispatch(startDeleteNoOrgCaretaker());
        }
    }

    const onEditCaretaker = () => {
        if (selectedCaretaker != null) {
            navigate(`/${RouteNames.individuals}/caretakers/${selectedCaretaker.id}`);
        }
    }

    const onReload = () => {

        dispatch(fetchNoOrgCaretakers({}));
    }

    const onSearch = () => {
        dispatch(fetchNoOrgCaretakers({q: searchTerm}));
    }

    const onRow = (caretaker: CaretakerResponseDto, index: number | undefined) => {
        return {
            onClick: () => {
                dispatch(selectNoOrgCaretaker(caretaker))
            }, // click row
        }
    }

    const onDeleteOrganizationAdmin = () => {
        if (selectedCaretaker != null) {
            dispatch(deleteNoOrgCaretaker({
                caretakerId: selectedCaretaker.id!
            }));
        }
    }

    const getRowClassName = (caretaker: CaretakerResponseDto, index: number) => {
        return selectedCaretaker != null && selectedCaretaker.id === caretaker.id ? 'bg-selected' : '';
    }

    const columns: ColumnsType<CaretakerResponseDto> = [
        {

            // width: '100%',
            title: t("column.firstName"),
            dataIndex: 'firstName',
            key: 'firstName',
            sorter: (a, b) => a.firstName! > b.firstName! ? -1 : 1,
        },
        {

            //width: '100%',
            title: t("column.lastName"),
            dataIndex: 'lastName',
            key: 'lastName',
            sorter: (a, b) => a.firstName! > b.firstName! ? -1 : 1,
        },
        {

            //width: '100%',
            title: t("column.email"),
            dataIndex: 'userAccount',
            key: 'email',
            sorter: (a, b) => a.userAccount?.emailAddress! > b.userAccount?.emailAddress! ? -1 : 1,
            render: (userAccount?: UserAccountDto) => userAccount?.emailAddress
        },
        {

            //width: '100%',
            title: t("column.phone"),
            dataIndex: 'userAccount',
            key: 'phone',
            sorter: (a, b) => a.userAccount?.mobileNumber! > b.userAccount?.mobileNumber! ? -1 : 1,
            render: (userAccount?: UserAccountDto) => formatPhone(userAccount?.mobileNumber, countries)
        },

    ];

    const content = status === 'loading' || status === 'idle'
        ? <TableSkeleton />
        : <>
            <CrudToolbar
                hasSelection={selectedCaretaker != null}
                canEdit={selectedCaretaker?.id != null}
                selectionLabel={[selectedCaretaker?.firstName, selectedCaretaker?.lastName].filter(Boolean).join(" ")}
                onDelete={onWarnDeleteCaretaker}
                onEdit={onEditCaretaker}
                onReload={onReload}
                onClearSelection={() => dispatch(selectNoOrgCaretaker(undefined))}
                search={{
                    placeholder: t("placeholder.searchPeople"),
                    value: searchTerm,
                    onChange: value => dispatch(updateNoOrgCaretakerSearchTerm(value)),
                    onSearch
                }}
            />
            <div className="card card--flush">
                <Table
                    rowKey={(caretaker: CaretakerResponseDto) => caretaker.id!}
                    rowClassName={getRowClassName}
                    onRow={onRow}
                    dataSource={caretakers}
                    columns={columns}
                    locale={{ emptyText: <EmptyState /> }}
                    pagination={{ hideOnSinglePage: true, defaultPageSize: 12, showSizeChanger: false }}
                />
            </div>

            <CrudModals
                status={status}
                error={error}
                entityKey="caretaker"
                deleteTarget={[selectedCaretaker?.firstName, selectedCaretaker?.lastName].filter(Boolean).join(" ")}
                onDismiss={() => dispatch(clearErrorNoOrgCaretakers())}
                onConfirmDelete={onDeleteOrganizationAdmin}
            />
        </>

    return content;
}
